import React from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type BadgeColor =
  | 'gray'
  | 'green'
  | 'blue'
  | 'amber'
  | 'red'
  | 'purple'
  | string;

export interface BadgeProps {
  color?: BadgeColor;
  solid?: boolean;
  size?: 'sm';
  dot?: boolean;
  leftIcon?: IconName;
  children?: React.ReactNode;
  className?: string;
}

export function Badge({
  color = 'gray',
  solid,
  size,
  dot,
  leftIcon,
  children,
  className = '',
}: BadgeProps) {
  const cls = ['badge', 'badge-' + color];
  if (solid) cls.push('badge-solid');
  if (size === 'sm') cls.push('badge-sm');
  if (className) cls.push(className);
  return (
    <span className={cls.join(' ')}>
      {dot && <span className="dot" />}
      {leftIcon && <Icon name={leftIcon} size={size === 'sm' ? 11 : 12} />}
      {children}
    </span>
  );
}

export const STATUS: Record<string, [string, string]> = {
  active: ['green', 'Active'],
  live: ['green', 'Live'],
  published: ['green', 'Published'],
  upcoming: ['blue', 'Upcoming'],
  draft: ['gray', 'Draft'],
  disabled: ['gray', 'Disabled'],
  pending: ['amber', 'Pending'],
  past: ['gray', 'Past'],
  cancelled: ['red', 'Cancelled'],
  full: ['amber', 'Full'],
  pass: ['green', 'Pass'],
  fail: ['red', 'Fail'],
};

export interface StatusBadgeProps {
  status: string;
  solid?: boolean;
  size?: 'sm';
}

export function StatusBadge({ status, solid, size }: StatusBadgeProps) {
  const [color, label] = STATUS[status] || ['gray', status];
  return (
    <Badge color={color} solid={solid} size={size} dot>
      {label}
    </Badge>
  );
}

export const USER_TYPE: Record<string, [string, string]> = {
  sample: ['blue', 'Sample'],
  test: ['purple', 'Test'],
};

export interface UserTypeBadgeProps {
  type?: string;
  size?: 'sm';
}

export function UserTypeBadge({ type, size }: UserTypeBadgeProps) {
  if (!type || type === 'normal') return null;
  const [color, label] = USER_TYPE[type] || ['gray', type];
  return (
    <Badge color={color} size={size}>
      {label}
    </Badge>
  );
}

export default Badge;
